import { ReactNode, useRef } from 'react';

import { useCollapsingHeaderOnScroll } from '../../hooks/useCollapsingHeaderOnScroll';
import { useListHeaderMetrics } from '../../hooks/useListHeaderMetrics';
import { ListScrollArea, ListSpacing } from './collapsibleListLayout.styled';
import { handleListKeyDown } from './listKeyboardNavigation';

interface CollapsibleListLayoutProperties {
  /** Search + sort block. Slides out of view on scroll down, back in on scroll up. */
  header: ReactNode;
  /** Optional row that stays visible while `header` hides — e.g. the week/date strip. */
  pinnedHeader?: ReactNode;
  children: ReactNode;
  /** Space between list items. */
  contentGap?: ListSpacing;
  /** Space between the header block and the list; the page gutter keeps it symmetric with the gutter above. */
  headerGap?: ListSpacing;
  ownsTopGutter?: boolean;
  className?: string;
  contentClassName?: string;
  testId?: string;
}

const CollapsibleListLayout = ({
  header,
  pinnedHeader,
  children,
  contentGap = 10,
  headerGap = 'gutter',
  ownsTopGutter = true,
  className = '',
  contentClassName = '',
  testId = 'collapsible-list',
}: CollapsibleListLayoutProperties) => {
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const collapsibleRef = useRef<HTMLDivElement>(null);

  useCollapsingHeaderOnScroll({ scrollAreaRef, headerRef, collapsibleRef });
  useListHeaderMetrics(headerRef, collapsibleRef, scrollAreaRef);

  return (
    <ListScrollArea
      ref={scrollAreaRef}
      $contentGap={contentGap}
      $headerGap={headerGap}
      className={`list-scroll-area ${ownsTopGutter ? 'scroll-page-gutter' : ''} ${className}`
        .replace(/\s+/g, ' ')
        .trim()}
      data-testid={testId}
      onKeyDown={handleListKeyDown}
    >
      <div
        ref={headerRef}
        className="list-header"
        data-collapsed="false"
        data-testid={`${testId}-header`}
      >
        <div ref={collapsibleRef} className="list-header-collapsible">
          {header}
        </div>
        {pinnedHeader && <div className="list-header-pinned">{pinnedHeader}</div>}
      </div>
      <div className={`list-content ${contentClassName}`.trim()} data-testid={`${testId}-content`}>
        {children}
      </div>
    </ListScrollArea>
  );
};

export default CollapsibleListLayout;
